type TimerHandle = ReturnType<typeof setTimeout>;

type GateRecord = {
  owners: Map<symbol, string>;
  listeners: Set<() => void>;
  generation: number;
};

const captureDrainGates = new Map<string, GateRecord>();

const gateFor = (jobId: string): GateRecord => {
  const current = captureDrainGates.get(jobId);
  if (current) return current;
  const created: GateRecord = { owners: new Map(), listeners: new Set(), generation: 0 };
  captureDrainGates.set(jobId, created);
  return created;
};

const publishGateChange = (jobId: string, record: GateRecord) => {
  record.generation += 1;
  for (const listener of [...record.listeners]) listener();
  if (record.owners.size === 0 && record.listeners.size === 0) captureDrainGates.delete(jobId);
};

/**
 * One lifetime can own several narrow native writes but exposes only one bit to
 * the capture scheduler. Keeping the owner after its React view unmounts is the
 * important part: an admitted save continues to be a writer until its own
 * `finally` settles, even when the operator has already opened Settings.
 */
export class CaptureDrainGateOwner {
  private readonly token = Symbol('capture-drain-gate-owner');
  private blocked = false;
  private readonly jobId: string;
  private readonly label: string;

  constructor(jobId: string, label: string) {
    this.jobId = jobId;
    this.label = label;
  }

  setBlocked(blocked: boolean): void {
    if (blocked === this.blocked) {
      // A second save can start and settle while another lane keeps this owner's
      // aggregate bit true. Its generation still invalidates a layout catch-up
      // admitted before that save.
      if (blocked) publishGateChange(this.jobId, gateFor(this.jobId));
      return;
    }
    this.blocked = blocked;
    const record = gateFor(this.jobId);
    if (blocked) record.owners.set(this.token, this.label);
    else record.owners.delete(this.token);
    publishGateChange(this.jobId, record);
  }

  blockedByAnotherOwner(): boolean {
    return captureDrainBlocked(this.jobId, this.token);
  }

  /**
   * A view going away is not permission to release an admitted writer. An idle
   * owner retires now; a busy one retires when its surviving async tail reports
   * idle through `setBlocked(false)`.
   */
  retireAfterSettled(): void {
    if (!this.blocked) this.remove();
  }

  private remove(): void {
    const record = captureDrainGates.get(this.jobId);
    if (!record) return;
    record.owners.delete(this.token);
    publishGateChange(this.jobId, record);
  }
}

export const captureDrainBlocked = (jobId: string, except?: symbol): boolean => {
  const owners = captureDrainGates.get(jobId)?.owners;
  if (!owners?.size) return false;
  if (!except) return true;
  for (const token of owners.keys()) if (token !== except) return true;
  return false;
};

export const captureDrainGeneration = (jobId: string): number =>
  captureDrainGates.get(jobId)?.generation ?? 0;

export const subscribeCaptureDrainGate = (jobId: string, listener: () => void): (() => void) => {
  const record = gateFor(jobId);
  record.listeners.add(listener);
  return () => {
    const current = captureDrainGates.get(jobId);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.owners.size === 0 && current.listeners.size === 0) captureDrainGates.delete(jobId);
  };
};

export type CaptureDrainCandidate = {
  id: string;
  status: 'pending' | 'failed' | 'held';
};

/**
 * The next capture that may be read, in queue order. A held capture (kept
 * pages of an interrupted scan, waiting for the operator) is a barrier:
 * nothing queued after it is read ahead of it, because pages scanned after a
 * stop may continue the document it ends with.
 */
export const nextDrainableCapture = <TCapture extends CaptureDrainCandidate>(
  captures: readonly TCapture[],
  eligible: (capture: TCapture) => boolean = () => true,
): TCapture | undefined => {
  for (const capture of captures) {
    if (capture.status === 'held') return undefined;
    if (capture.status === 'pending' && eligible(capture)) return capture;
  }
  return undefined;
};

export type CaptureWaveCandidate = {
  id: string;
  waveMode?: 'accelerated' | 'processor-paced';
  folderImport?: { id: string };
};

/**
 * Whether `next` may start while `inFlight` captures are still being processed.
 *
 * One capture runs alone until the batch exists. After that an accelerated wave
 * may start READING whenever every accelerated wave in flight has moved on to
 * extraction, up to three at once: overlapping one wave's page reading with
 * earlier waves' extraction was measured at +24% end to end (49.7 -> 61.7
 * pages/min).
 *
 * A wave of a folder import is different: the folder's finite preparation has
 * already read its pages before it starts, so it reaches extraction at once and
 * there is no reading left to overlap. Starting it beside an earlier wave of the
 * same folder only splits the reader between them, and the earlier wave's
 * documents then land no sooner than the later waves'. Measured on the owner's
 * GPU import (2026-09-29, 2 + 40 + 40 + 18 files): waves 2-4 all started within
 * 240 ms, and after the first two documents nothing reached the workspace for
 * 2.6 minutes, then all 98 landed within 67 s. So waves of one folder import run
 * in order, each alone.
 */
export const mayStartCaptureWave = (
  next: CaptureWaveCandidate,
  inFlight: readonly CaptureWaveCandidate[],
  state: { durableBatchReady: boolean; reachedExtraction: (captureId: string) => boolean },
): boolean => {
  if (inFlight.length === 0) return true;
  if (
    next.waveMode !== 'accelerated' ||
    inFlight.some((capture) => capture.waveMode !== 'accelerated')
  )
    return false;
  const folder = next.folderImport?.id;
  if (folder && inFlight.some((capture) => capture.folderImport?.id === folder)) return false;
  return (
    inFlight.length < 3 &&
    state.durableBatchReady &&
    inFlight.every((capture) => state.reachedExtraction(capture.id))
  );
};

export type AppLifetimeCaptureDrainOptions<TCapture extends CaptureDrainCandidate> = {
  list: () => Promise<TCapture[]>;
  process: (capture: TCapture) => Promise<boolean>;
  blocked: () => boolean;
  onError: (reason: unknown) => void;
  pollMilliseconds?: number;
  schedule?: (action: () => void, delayMilliseconds: number) => TimerHandle;
  cancel?: (timer: TimerHandle) => void;
};

/**
 * The selected job's durable manifests outlive every page in the renderer. This
 * small controller does too. It owns no document/view state; it only asks for
 * the next pending manifest while the Documents view is absent.
 */
export class AppLifetimeCaptureDrain<TCapture extends CaptureDrainCandidate> {
  private active = false;
  private disposed = false;
  private pumping = false;
  private wakeRequested = false;
  private timer: TimerHandle | null = null;
  private readonly pollMilliseconds: number;
  private readonly schedule: (action: () => void, delayMilliseconds: number) => TimerHandle;
  private readonly cancel: (timer: TimerHandle) => void;
  private readonly options: AppLifetimeCaptureDrainOptions<TCapture>;

  constructor(options: AppLifetimeCaptureDrainOptions<TCapture>) {
    this.options = options;
    this.pollMilliseconds = options.pollMilliseconds ?? 750;
    this.schedule = options.schedule ?? ((action, delay) => setTimeout(action, delay));
    this.cancel = options.cancel ?? ((timer) => clearTimeout(timer));
  }

  setBackgroundActive(active: boolean): void {
    if (this.disposed) return;
    this.active = active;
    if (!active) {
      if (this.timer !== null) this.cancel(this.timer);
      this.timer = null;
      return;
    }
    this.wake();
  }

  wake(): void {
    if (this.disposed || !this.active) return;
    if (this.timer !== null) this.cancel(this.timer);
    this.timer = null;
    if (this.pumping) {
      this.wakeRequested = true;
      return;
    }
    void this.pump();
  }

  dispose(): void {
    this.disposed = true;
    this.active = false;
    if (this.timer !== null) this.cancel(this.timer);
    this.timer = null;
  }

  private pollLater(): void {
    if (this.disposed || !this.active || this.timer !== null) return;
    this.timer = this.schedule(() => {
      this.timer = null;
      this.wake();
    }, this.pollMilliseconds);
  }

  private async pump(): Promise<void> {
    if (this.pumping || this.disposed || !this.active) return;
    this.pumping = true;
    try {
      while (!this.disposed && this.active) {
        if (this.options.blocked()) return;
        const captures = await this.options.list();
        if (this.disposed || !this.active) return;
        const next = nextDrainableCapture(captures);
        if (!next) {
          this.pollLater();
          return;
        }
        if (this.options.blocked()) return;
        const dispatched = await this.options.process(next);
        if (!dispatched) {
          this.pollLater();
          return;
        }
      }
    } catch (reason) {
      this.options.onError(reason);
      this.pollLater();
    } finally {
      this.pumping = false;
      if (this.wakeRequested) {
        this.wakeRequested = false;
        this.wake();
      }
    }
  }
}
